/*
 * Both steps share one `<FormDialog>` whose body and footer swap, rather than opening a
 * sub-modal: stacking two Radix Dialogs leaks z-index and portal interactions that
 * overlap content in some scrollable parents.
 *
 * The parent must mount this as
 * `{intake.isMounted && <BriefIntakeModal key={intake.instanceKey} ... />}`. The `key` is
 * what resets the form state; `isMounted` keeps the node in the tree long enough for the
 * exit animation. Guarding on `isOpen` unmounts in the same tick as the close and skips
 * the exit entirely.
 */
import { useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import clsx from 'clsx';
import {
  ExistingArtifactsPicker,
  resolveWorkspaceArtifactSort,
  workspaceArtifactsInfiniteQuery,
  type WorkspaceArtifactRow,
} from '@/entities/artifact';
import { BRIEF_TYPE_LABEL } from '@/entities/brief';
import {
  briefTemplateLevelLabel,
  briefTemplatesQueryOptions,
  type BriefTemplate,
} from '@/entities/brief-template';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { FormDialog } from '@/shared/ui/FormDialog';
import type { BriefArtifactRef, KnowledgeBaseRef } from '../../model/brief-generation.types';
import { useBriefIntakeForm } from '../../model/useBriefIntakeForm';
import { BriefIntakeComposer } from '../BriefIntakeComposer';
import { BRIEF_INTAKE_ACCEPTED_FORMATS } from './BriefIntakeModal.constants';
import type { BriefIntakeModalProps } from './BriefIntakeModal.types';
import { groupBriefTemplatesByLevel } from './groupBriefTemplatesByLevel';
import { useTranslation } from 'react-i18next';

type Step = 'form' | 'picker';

/*
 * Radix requires every item to carry a value, so these sentinels must not collide with a
 * template id. The message rows are disabled items rather than plain text: Radix skips
 * disabled items in arrow navigation and typeahead, and rendering an item at all closes
 * the library's `:has()` gate, so its built-in empty row does not print the same sentence
 * underneath.
 */
const CREATE_TEMPLATE_VALUE = '__create-brief-template__';
const NO_TEMPLATES_VALUE = '__no-brief-templates__';
const RETRY_TEMPLATES_VALUE = '__retry-brief-templates__';

function renderTemplateOption(template: BriefTemplate) {
  return (
    <SelectItem key={template.id} value={template.id}>
      <div className="flex flex-col">
        <span>{template.name}</span>
        {template.briefTypes.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {template.briefTypes.map((briefType) => BRIEF_TYPE_LABEL[briefType]).join(', ')}
          </span>
        )}
      </div>
    </SelectItem>
  );
}

export const BriefIntakeModal = ({
  open,
  onClose,
  onGenerate,
  initialArtifacts,
}: BriefIntakeModalProps) => {
  const { t } = useTranslation('briefs');

  const form = useBriefIntakeForm(initialArtifacts);
  const levelsEnabled = useBooleanFlagValue('brief_template_levels', false);
  const fileInputId = useId();
  const templateFieldId = useId();
  const titleFieldId = useId();

  const {
    data: templates = [],
    isPending: isLoadingTemplates,
    isError: templatesRequestFailed,
    isFetching: isFetchingTemplates,
    refetch: refetchTemplates,
  } = useQuery(
    briefTemplatesQueryOptions(
      // A turned-off template stays published, so only `enabled` keeps it out of the picker.
      levelsEnabled ? { status: 'published', enabled: true } : { status: 'published' },
    ),
  );

  /*
   * A retry leaves the panel open, so the failure rows give way to the loading rows while
   * the request is in flight — otherwise the panel would sit on the same error the user
   * just asked us to clear, and the press would read as having done nothing.
   */
  const templatesAreLoading = isLoadingTemplates || (templatesRequestFailed && isFetchingTemplates);
  const templatesFailed = templatesRequestFailed && !isFetchingTemplates;

  /*
   * Gated on the same permission as the destination route, so the way out of the empty
   * panel is never offered to someone `ProtectedRoute` would bounce.
   */
  const { canManageBriefTemplates } = usePermissions();
  const navigate = useNavigate();

  /*
   * A fallback that should never render, since every state below supplies items of its
   * own. It stays because passing `loading` obliges `emptyLabel`.
   */
  const templatesEmptyLabel = t('briefGeneration.briefIntakeModal.noTemplatesPublished');

  /*
   * Every state below is built out of options, which is not the prettiest shape for these
   * rows: a designed empty state would be centred prose with a link, not two list entries
   * borrowing an option's padding and hover. Keyboard reach is what forces it. Radix
   * cancels Tab inside the listbox, so a link there is focusable and still unreachable,
   * and arrow keys only ever land on options. Anything nicer to look at would be reachable
   * by pointer alone, so the plainer rows win.
   *
   * Creating is offered only when the list is genuinely empty: on failure the templates
   * may well exist, and inviting a duplicate is the conflation the error message is there
   * to prevent, so that state offers a retry instead.
   */
  const templatesAreEmpty =
    !templatesRequestFailed && !templatesAreLoading && templates.length === 0;

  const [step, setStep] = useState<Step>('form');

  // Lifted here so a picker → form → picker round-trip preserves the user's search,
  // selection, filter and sort.
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelectedIds, setPickerSelectedIds] = useState<Set<string>>(() => new Set());
  const [pickerKindFilter, setPickerKindFilter] = useState<ArtifactKind | null>(null);
  const [pickerSort, setPickerSort] = useState<string | null>(null);

  const workspaceId = useCurrentWorkspaceId();

  // Same query key as the picker, so Add can map `selectedIds` to `BriefArtifactRef[]`
  // with no extra request. The search MUST be debounced and trimmed exactly as the picker
  // does it, or the keys diverge while the user types and this observes a different query
  // than the one filling the rows.
  const debouncedPickerSearch = useDebounce(pickerSearch, SEARCH_DEBOUNCE_MS);
  const trimmedPickerSearch = debouncedPickerSearch.trim();
  const pickerSortParams = resolveWorkspaceArtifactSort(pickerSort);

  const { data: pickerCacheData } = useInfiniteQuery(
    workspaceArtifactsInfiniteQuery(workspaceId ?? undefined, {
      kind: pickerKindFilter ?? undefined,
      search: trimmedPickerSearch || undefined,
      sortBy: pickerSortParams?.sortBy,
      sortOrder: pickerSortParams?.sortOrder,
    }),
  );

  const pickerLoadedRows = useMemo<WorkspaceArtifactRow[]>(
    // The SDK yields `ArtifactListItemDto[]`, which the picker narrows with the same
    // two-step cast; both have to agree until the SDK includes the optional fields.
    () =>
      (pickerCacheData?.pages.flatMap((p) => p.artifacts) ??
        []) as unknown as WorkspaceArtifactRow[],
    [pickerCacheData],
  );

  // Nothing is dimmed as already attached: every workspace artifact is selectable for a
  // brief. Stable reference so the picker's memos do not see a new set each render.
  const emptyAttachedIds = useMemo(() => new Set<string>(), []);

  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  const handleGenerate = () => {
    if (!form.isValid) return;
    onGenerate(form.getSubmitPayload());
  };

  /*
   * Radix closes the panel on any selection. Retrying is not one: the user is asking to
   * see the list, so the close that follows the sentinel is swallowed and the panel stays
   * up over the loading rows.
   */
  const [isTemplateMenuOpen, setIsTemplateMenuOpen] = useState(false);
  const keepTemplateMenuOpenRef = useRef(false);

  const handleTemplateMenuOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && keepTemplateMenuOpenRef.current) {
      keepTemplateMenuOpenRef.current = false;
      return;
    }
    setIsTemplateMenuOpen(nextOpen);
  };

  const handleTemplateChange = (next: string) => {
    if (next === CREATE_TEMPLATE_VALUE) {
      void navigate(ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATES);
      return;
    }
    if (next === RETRY_TEMPLATES_VALUE) {
      keepTemplateMenuOpenRef.current = true;
      void refetchTemplates();
      return;
    }
    const template = templates.find((t) => t.id === next);
    form.setTemplate(next, template?.name ?? null);
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    form.addFiles(Array.from(event.target.files));
    // Reset so re-attaching the same file still fires onChange.
    event.target.value = '';
  };

  const triggerUpload = () => {
    document.getElementById(fileInputId)?.click();
  };

  const handlePickerBack = () => {
    setStep('form');
  };

  const handlePickerAdd = () => {
    if (pickerSelectedIds.size === 0) return;
    const refs: BriefArtifactRef[] = pickerLoadedRows
      .filter((row) => pickerSelectedIds.has(row.id))
      .map((row) => ({
        id: row.id,
        name: row.name,
        kind: row.kind,
      }));
    form.addArtifacts(refs);
    // Selection resets after committing; the search is deliberately kept.
    setPickerSelectedIds(new Set());
    setStep('form');
  };

  const handleKbToggle = (kb: KnowledgeBaseRef) => {
    const exists = form.values.knowledgeBases.some((k) => k.id === kb.id);
    if (exists) {
      form.removeKnowledgeBase(kb.id);
    } else {
      form.addKnowledgeBases([kb]);
    }
  };

  const isFormStep = step === 'form';
  const isPickerStep = step === 'picker';
  const title = isPickerStep
    ? t('briefTemplateCreation.briefTemplateIntakeModal.addExistingArtifacts')
    : t('briefGeneration.briefGenerationProgressModal.newBrief');

  const pickerSelectedCount = pickerSelectedIds.size;
  const pickerAddLabel =
    pickerSelectedCount > 0
      ? t('briefGeneration.briefIntakeModal.addSelected', { selectedCount: pickerSelectedCount })
      : t('briefTemplateCreation.briefTemplateIntakeModal.add');

  return (
    // The height cap keeps the picker's rows scrolling inside the dialog instead of
    // pushing it past the viewport. The form step's narrower width must be set with `w-`,
    // not `max-w-`: DialogContent's base is a `w-` utility, which `max-w-` cannot
    // override, and the dialog collapses instead.
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      className={clsx(
        isPickerStep
          ? 'flex h-[min(850px,calc(100dvh-64px))] max-h-[calc(100dvh-64px)] flex-col'
          : 'flex max-h-[calc(100dvh-64px)] w-[min(calc(100vw-2rem),540px)] flex-col',
      )}
    >
      <FormDialog.Body className="flex min-h-0 flex-1 flex-col">
        {isFormStep ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5 mt-1">
              <Label htmlFor={templateFieldId}>
                {t('briefGeneration.briefIntakeModal.briefTemplate')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Select
                open={isTemplateMenuOpen}
                onOpenChange={handleTemplateMenuOpenChange}
                value={form.values.templateId ?? ''}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger
                  id={templateFieldId}
                  aria-label={t('briefGeneration.briefIntakeModal.selectTemplate')}
                  className="bg-input"
                >
                  <SelectValue placeholder={t('briefGeneration.briefIntakeModal.selectTemplate')}>
                    {form.values.templateName}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent
                  loading={templatesAreLoading}
                  loadingLabel={t('briefGeneration.briefIntakeModal.loadingTemplates')}
                  emptyLabel={templatesEmptyLabel}
                >
                  {templatesFailed && (
                    <>
                      <SelectItem
                        value={NO_TEMPLATES_VALUE}
                        disabled
                        className="justify-center px-2 text-center"
                      >
                        {t('briefGeneration.briefIntakeModal.couldNotLoadTemplates')}
                      </SelectItem>
                      <SelectItem
                        value={RETRY_TEMPLATES_VALUE}
                        className="justify-center px-2 text-center"
                      >
                        {t('briefGeneration.briefIntakeModal.tryAgain')}
                      </SelectItem>
                    </>
                  )}
                  {templatesAreEmpty && (
                    <>
                      {/* `px-2` drops the gutter the item reserves for its check indicator;
                          neither row can be selected, so it would only offset the centred text. */}
                      <SelectItem
                        value={NO_TEMPLATES_VALUE}
                        disabled
                        className="justify-center px-2 text-center"
                      >
                        {t('briefGeneration.briefIntakeModal.noTemplatesPublished')}
                      </SelectItem>
                      {canManageBriefTemplates && (
                        <SelectItem
                          value={CREATE_TEMPLATE_VALUE}
                          className="justify-center px-2 text-center"
                        >
                          {t('briefGeneration.briefIntakeModal.createAndPublishOne')}
                        </SelectItem>
                      )}
                    </>
                  )}
                  {levelsEnabled
                    ? groupBriefTemplatesByLevel(templates).map((group) => (
                        <SelectGroup key={group.level}>
                          <SelectLabel>{briefTemplateLevelLabel(group.level)}</SelectLabel>
                          {group.templates.map(renderTemplateOption)}
                        </SelectGroup>
                      ))
                    : templates.map(renderTemplateOption)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={titleFieldId}>
                {t('briefGeneration.briefIntakeModal.title')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id={titleFieldId}
                placeholder={t('briefGeneration.briefIntakeModal.enterBriefTitle')}
                value={form.values.title}
                onChange={(event) => form.setTitle(event.target.value)}
                className="bg-input"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>
                {t('briefGeneration.briefIntakeModal.addContext')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <BriefIntakeComposer
                instructions={form.values.instructions}
                onInstructionsChange={form.setInstructions}
                files={form.values.files}
                artifacts={form.values.artifacts}
                onRemoveFile={form.removeFile}
                onRemoveArtifact={form.removeArtifact}
                onUploadFile={triggerUpload}
                onBrowseArtifacts={() => setStep('picker')}
                knowledgeBases={form.values.knowledgeBases}
                onToggleKnowledgeBase={handleKbToggle}
                onClearKnowledgeBases={form.clearKnowledgeBases}
              />
            </div>

            <input
              id={fileInputId}
              type="file"
              multiple
              accept={BRIEF_INTAKE_ACCEPTED_FORMATS}
              aria-label={t('briefGeneration.briefIntakeModal.attachFiles')}
              className="sr-only"
              onChange={handleFileInput}
            />
          </div>
        ) : workspaceId ? (
          <ExistingArtifactsPicker
            workspaceId={workspaceId}
            attachedIds={emptyAttachedIds}
            selectedIds={pickerSelectedIds}
            kindFilter={pickerKindFilter}
            searchInput={pickerSearch}
            sort={pickerSort}
            isUserInput={false}
            onSelectedIdsChange={setPickerSelectedIds}
            onKindFilterChange={setPickerKindFilter}
            onSearchInputChange={setPickerSearch}
            onSortChange={setPickerSort}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('briefGeneration.briefIntakeModal.selectAWorkspaceToContinue')}
          </p>
        )}
      </FormDialog.Body>
      {isFormStep ? (
        <FormDialog.Footer
          primary={{
            label: t('briefGeneration.briefIntakeModal.generate'),
            onClick: handleGenerate,
            disabled: !form.isValid,
          }}
        />
      ) : (
        <FormDialog.Footer
          primary={{
            label: pickerAddLabel,
            onClick: handlePickerAdd,
            disabled: pickerSelectedCount === 0,
          }}
          secondary={{
            label: t('briefGeneration.briefIntakeModal.back'),
            onClick: handlePickerBack,
          }}
        />
      )}
    </FormDialog>
  );
};
